import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Avatar,
  Typography,
  Divider,
  Button,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ListItemSecondaryAction,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  ReportProblem as ReportProblemIcon,
  FilterList as FilterIcon,
  ClearAll as ClearAllIcon,
  Delete as DeleteIcon,
  MarkAsUnread as MarkAsUnreadIcon,
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useEventContext, Event, EventFilter } from '../../contexts/EventContext';
import { useTheme } from '@mui/material/styles';

// 알림 타입 정의
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
  read: boolean;
  link?: string;
}

interface EventNotificationProps {
  notifications?: Notification[];
  onNotificationClick?: (notification: Notification) => void;
  onMarkAllAsRead?: () => void;
  onClearAll?: () => void;
  maxHeight?: number;
  showStatusFilter?: boolean;
  showTypeFilter?: boolean;
  autoNavigateOnClick?: boolean;
  maxNotifications?: number;
  poolId?: string;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
}

const EventNotification: React.FC<EventNotificationProps> = ({
  maxHeight = 400,
  showStatusFilter = true,
  showTypeFilter = true,
  autoNavigateOnClick = true,
  maxNotifications = 5,
  poolId,
  position = { vertical: 'bottom', horizontal: 'right' },
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();

  // EventContext에서 데이터와 함수 가져오기
  const { 
    events, 
    filteredEvents, 
    unreadCount, 
    loading, 
    error,
    updateEventStatus,
    acknowledgeEvent,
    markAsRead,
    markAllAsRead,
    fetchEvents,
    deleteEvent,
    clearEvents,
    setFilter,
    clearFilter,
    currentFilter,
  } = useEventContext();

  // 초기화 시 및 poolId가 변경될 때 필터 설정
  useEffect(() => {
    if (poolId) {
      setFilter({ ...currentFilter, poolId });
    }
  }, [poolId, setFilter, currentFilter]);

  // 메뉴 열기/닫기 핸들러
  const handleNotificationsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setAnchorEl(null);
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // 알림 클릭 핸들러
  const handleNotificationClick = (event: Event) => {
    markAsRead(event.id);
    
    // 이벤트 유형에 따라 다른 페이지로 이동
    if (autoNavigateOnClick) {
      let targetPath = '';
      
      switch (event.type) {
        case 'danger':
          targetPath = `/alerts/incident/${event.id}`;
          break;
        case 'warning':
          targetPath = `/pool-monitoring/${event.poolId}`;
          break;
        case 'info':
        default:
          targetPath = `/alerts`;
          break;
      }
      
      if (targetPath) {
        navigate(targetPath);
      }
    }
    
    handleNotificationsClose();
  };

  // 이벤트 유형 필터 핸들러
  const handleTypeFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setFilter({
      ...currentFilter,
      types: value as Array<'danger' | 'warning' | 'info'>,
    });
  };

  // 이벤트 상태 필터 핸들러
  const handleStatusFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setFilter({
      ...currentFilter,
      status: value as Array<'new' | 'processing' | 'resolved'>,
    });
  };

  // 읽지 않은 이벤트만 표시하기
  const toggleUnreadFilter = () => {
    setFilter({
      ...currentFilter,
      isRead: currentFilter.isRead === false ? undefined : false,
    });
  };

  // 필터 모두 지우기
  const handleClearFilters = () => {
    clearFilter();
    setFilterAnchorEl(null);
    setSnackbarMessage('모든 필터가 제거됨');
    setSnackbarOpen(true);
  };

  // 이벤트 새로고침
  const handleRefreshEvents = () => {
    fetchEvents();
    setSnackbarMessage('이벤트가 새로고침됨');
    setSnackbarOpen(true);
  };

  // 이벤트 삭제 핸들러
  const handleDeleteEvent = async (eventId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await deleteEvent(eventId);
      setSnackbarMessage('이벤트가 삭제 완료');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('이벤트 삭제에 실패');
      setSnackbarOpen(true);
    }
  };

  const handleAck = async (eventId: string, falsePositive: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await acknowledgeEvent(eventId, falsePositive);
      setSnackbarMessage(falsePositive ? '오탐으로 처리함' : '이벤트를 확인함');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('이벤트 확인에 실패');
      setSnackbarOpen(true);
    }
  };

  // 상태 업데이트 핸들러
  const handleUpdateStatus = (eventId: string, status: 'new' | 'processing' | 'resolved', event: React.MouseEvent) => {
    event.stopPropagation();
    updateEventStatus(eventId, status);
    setSnackbarMessage(`이벤트 상태가 ${status}로 변경 완료`);
    setSnackbarOpen(true);
  };

  // 이벤트 아이콘 가져오기
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'danger':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'info':
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
      default:
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  // 이벤트 상태 칩 표시
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'new':
        return <Chip size="small" label="새 알림" color="error" sx={{ ml: 1 }} />;
      case 'processing':
        return <Chip size="small" label="처리 중" color="warning" sx={{ ml: 1 }} />;
      case 'resolved':
        return <Chip size="small" label="해결됨" color="success" sx={{ ml: 1 }} />;
      default:
        return null;
    }
  };

  // 시간 표시 형식화
  const formatEventTime = (timestamp: Date) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 1) {
      return '방금 전';
    } else if (diffMins < 60) {
      return `${diffMins}분 전`;
    } else if (diffMins < 1440) {
      return `${Math.floor(diffMins / 60)}시간 전`;
    } else {
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  // 필터가 적용되었는지 확인
  const hasActiveFilters = () => {
    return (
      currentFilter.types?.length > 0 ||
      currentFilter.status?.length > 0 ||
      currentFilter.isRead !== undefined ||
      currentFilter.poolId !== undefined
    );
  };

  // 표시할 이벤트 목록
  const displayEvents = filteredEvents.slice(0, maxNotifications);

  return (
    <>
      <Tooltip title={`${unreadCount}개의 읽지 않은 알림`}>
        <IconButton 
          color="inherit" 
          onClick={handleNotificationsOpen}
          aria-label={`${unreadCount}개의 알림 있음`}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            overlap="circular"
            max={99}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{
          vertical: position.vertical,
          horizontal: position.horizontal,
        }}
        transformOrigin={{
          vertical: position.vertical === 'bottom' ? 'top' : 'bottom',
          horizontal: position.horizontal,
        }}
        PaperProps={{
          sx: { width: '360px', maxHeight: `${maxHeight}px` }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            알림 {unreadCount > 0 && <Chip size="small" label={unreadCount} color="error" />}
          </Typography>
          <Box>
            <Tooltip title="필터">
              <IconButton 
                size="small" 
                onClick={handleFilterOpen}
                color={hasActiveFilters() ? "primary" : "default"}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="새로고침">
              <IconButton 
                size="small" 
                onClick={handleRefreshEvents}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="모두 읽음 표시">
              <IconButton 
                size="small" 
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                <DoneAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="모두 지우기">
              <IconButton 
                size="small" 
                onClick={clearEvents}
                disabled={events.length === 0}
              >
                <ClearAllIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Divider />
        
        {filteredEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">알림 없음</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, overflow: 'auto', maxHeight: maxHeight - 120 }}>
            {displayEvents.map((event) => (
              <React.Fragment key={event.id}>
                <ListItem 
                  button 
                  onClick={() => handleNotificationClick(event)}
                  sx={event.isRead ? { opacity: 0.7 } : { bgcolor: 'action.hover' }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'background.paper' }}>
                      {getEventIcon(event.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle2" noWrap component="span">
                          {event.title}
                        </Typography>
                        {getStatusChip(event.status)}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography 
                          component="span" 
                          variant="body2" 
                          color="text.primary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mb: 0.5,
                          }}
                        >
                          {event.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {event.poolName}, {event.location}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatEventTime(event.timestamp)}
                          </Typography>
                        </Box>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {event.status !== 'resolved' && (
                      <>
                        <Tooltip title="확인">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleAck(event.id, false, e)}
                          >
                            <CheckCircleIcon fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="오탐">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleAck(event.id, true, e)}
                          >
                            <ReportProblemIcon fontSize="small" color="warning" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={(e) => handleDeleteEvent(event.id, e)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
        
        {filteredEvents.length > maxNotifications && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {filteredEvents.length - maxNotifications}개 더 있음
            </Typography>
          </Box>
        )}
        
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button 
            size="small" 
            onClick={() => { navigate('/alerts'); handleNotificationsClose(); }}
          >
            모든 알림 보기
          </Button>
        </Box>
      </Popover>

      {/* 필터 메뉴 */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="event-type-filter-label">이벤트 유형</InputLabel>
            <Select
              labelId="event-type-filter-label"
              id="event-type-filter"
              multiple
              value={currentFilter.types || []}
              onChange={handleTypeFilterChange}
              label="이벤트 유형"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={
                      value === 'danger' ? '위험' :
                      value === 'warning' ? '경고' : '정보'
                    } size="small" />
                  ))}
                </Box>
              )}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="danger">위험</MenuItem>
              <MenuItem value="warning">경고</MenuItem>
              <MenuItem value="info">정보</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
        
        <MenuItem>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="event-status-filter-label">이벤트 상태</InputLabel>
            <Select
              labelId="event-status-filter-label"
              id="event-status-filter"
              multiple
              value={currentFilter.status || []}
              onChange={handleStatusFilterChange}
              label="이벤트 상태"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={
                      value === 'new' ? '새 알림' :
                      value === 'processing' ? '처리 중' : '해결됨'
                    } size="small" />
                  ))}
                </Box>
              )}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="new">새 알림</MenuItem>
              <MenuItem value="processing">처리 중</MenuItem>
              <MenuItem value="resolved">해결됨</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
        
        <MenuItem onClick={toggleUnreadFilter}>
          <ListItemIcon>
            <MarkAsUnreadIcon fontSize="small" color={currentFilter.isRead === false ? "primary" : "inherit"} />
          </ListItemIcon>
          <ListItemText>읽지 않은 알림만 보기</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleClearFilters}>
          <ListItemIcon>
            <ClearAllIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>필터 초기화</ListItemText>
        </MenuItem>
      </Menu>

      {/* 스낵바 알림 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventNotification; 